import mimeDB from 'mime-db';
import { ACCEPTED_FILE_TYPES } from '@/utils/fileTypes';
import { FlowMode } from '@/enums/flow-modes.enum';
import { OAUTH_CALLBACK_QUERY_PARAMS } from './oauthCallbackParams';

/**
 * Appends elements to a URLSearchParams object.
 * @param key - The key to append the elements to.
 * @param arr - The array of elements to append.
 * @param searchParams - The URLSearchParams object to append to.
 * @returns A new URLSearchParams object with the elements appended.
 * @example
 * key = 'id'
 * arr = ['1', '2', '3']
 * will create search params for a URL in the form of ?id=1&id=2&id=3
 */
export const appendQsFromArray = (key: string, arr: (string | number | boolean)[], searchParams?: URLSearchParams) => {
  const copy = new URLSearchParams(searchParams || undefined); // dont allow null, 0
  arr.forEach((e) => copy.append(key, String(e)));
  return copy;
};

export const urlCanParse = (url: string) => {
  try {
    if ('canParse' in URL && typeof URL.canParse === 'function') {
      return URL.canParse(url);
    } else {
      new URL(url);
      return true;
    }
  } catch (_e) {
    return false;
  }
};

const SAFE_EXTERNAL_PROTOCOLS = new Set(['http:', 'https:']);

export const getSafeExternalUrl = (url?: string | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (!SAFE_EXTERNAL_PROTOCOLS.has(parsedUrl.protocol)) {
      return undefined;
    }

    return parsedUrl.toString();
  } catch (_error) {
    return undefined;
  }
};

/**
 * Narrows a caller-supplied post-sign-in destination to a same-origin relative path.
 *
 * The destination reaches React Router's `navigate()`, which forwards anything that parses as an
 * absolute URL untouched. `history.pushState` then rejects the cross-origin value and the router
 * falls back to `location.assign()`, so a `javascript:` destination executes in the authenticated
 * document. Resolving against our own origin also collapses the protocol-relative, backslash and
 * embedded-tab forms that would otherwise navigate off-site.
 *
 * The resolved path is rejected when it still starts with `//`: dot-segment removal turns inputs
 * like `/.//evil.example` into the pathname `//evil.example` on our own origin, so the origin
 * check passes while `location.assign()` would read the result as protocol-relative.
 */
export const getSafeRedirectPath = (path: string | null | undefined): string => {
  if (!path) {
    return '/';
  }

  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) {
      return '/';
    }

    const resolved = `${url.pathname}${url.search}${url.hash}`;
    return resolved.startsWith('//') ? '/' : resolved;
  } catch (_error) {
    return '/';
  }
};

export const getSignInReturnPath = ({
  location = window.location,
  view,
}: { location?: { pathname: string; search: string }; view?: FlowMode } = {}): string => {
  const params = new URLSearchParams(location.search);
  Object.values(OAUTH_CALLBACK_QUERY_PARAMS).forEach((param) => params.delete(param));
  if (view) {
    params.set('view', view);
  }
  const search = params.toString();
  return `${location.pathname}${search ? `?${search}` : ''}`;
};

type MimeDb = Record<string, { source?: string; charset?: string; compressible?: boolean; extensions?: string[] }>;

const LEGITIMATE_EXTENSIONS = [
  ...Object.values(mimeDB as MimeDb).flatMap((mime) => mime.extensions || []),
  ...Object.values(ACCEPTED_FILE_TYPES).flatMap((extensions) => extensions.map((extension) => extension.slice(1))),
].map((extension) => extension.toLocaleLowerCase());

/**
 * Parses RFC 6266 `Content-Disposition` header to extract the `filename` value.
 * Handles both quoted (`filename="x.mov"`) and unquoted (`filename=x.mov`) forms,
 * and the `filename*=UTF-8''x.mov` extended form. Returns undefined when no filename is set.
 */
export const parseFilenameFromContentDisposition = (header: string | undefined): string | undefined => {
  if (!header) {
    return;
  }

  const match = header.match(/filename\*?=(?:UTF-8'')?(?:"([^"]+)"|([^;]+))/i);
  const raw = match?.[1] ?? match?.[2]?.trim();
  if (!raw) {
    return;
  }

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

export const getFileExtension = (fileUrl: string, fileType: string): string => {
  if (urlCanParse(fileUrl)) {
    const url = new URL(fileUrl);
    const urlFileName = url.pathname.split('/').pop();
    const split = urlFileName?.split('.');
    const extension = split?.length && split.length > 1 ? split.pop() : '';
    if (extension && LEGITIMATE_EXTENSIONS.includes(extension.toLocaleLowerCase())) {
      return extension;
    }

    if (url.hostname.includes('cloudinary') || url.hostname.includes('media.weavy.ai')) {
      return url.pathname.includes('/image/') ? 'png' : 'mp4';
    }
  } else {
    const split = fileUrl.toLowerCase().split('.');
    const extension = split?.length && split.length > 1 ? split.pop()?.split('?')[0] : '';
    if (extension && LEGITIMATE_EXTENSIONS.includes(extension.toLocaleLowerCase())) {
      return extension;
    }
  }

  if (fileType) {
    switch (fileType) {
      case 'image':
        return 'png';
      case 'video':
        return 'mp4';
      default:
        return '';
    }
  }

  return '';
};

const VIDEO_EXTENSION_MIME_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/mp4',
  webm: 'video/webm',
};

/** MIME hint for a video `<source>`. Pass the durable URL; signed playback URLs have no extension. */
export const getVideoMimeType = (url: string | undefined): string | undefined =>
  url ? VIDEO_EXTENSION_MIME_TYPES[getFileExtension(url, 'video').toLowerCase()] : undefined;

export const getRecipeIdFromPath = (path: string) =>
  path.startsWith('/flow/') || path.startsWith('/tool/') ? path.split('/')[2] : undefined;

// The size we build for every group thumbnail. c_lfill center-crops to a 512 square and only ever
// shrinks, so a small original is shown at its real size instead of stretched into fake pixels. 512
// covers the biggest place we show a thumbnail (the ~160px save slot, even at 3x); object-fit: cover
// downscales it for the smaller sidebar and picker tiles. f_auto and q_auto pick the best format and
// compression: smaller files, and a HEIC image becomes one the browser can show.
const THUMBNAIL_DISPLAY_TRANSFORM = 'c_lfill,w_512,h_512,f_auto,q_auto';

// Resize a stored thumbnail url so it stays sharp on hi-dpi screens. Our media server bakes a small
// size step into the url (c_scale,w_150, or f_auto,q_auto for the view size); swap that step for the
// 512 one. We match the step by its comma, the shape our size steps always have, so a version like
// v123 or a plain folder is left in place. Only our own server at the very start of the url is
// touched, so an outside url, a look-alike host, or a non-image path is returned as-is.
export const buildThumbnailDisplayUrl = (url: string | null | undefined): string | undefined =>
  url
    ? url.replace(
        /^(https:\/\/media\.weavy\.ai\/image\/upload\/)([a-z]+_[^/]*,[^/]*\/)?/,
        `$1${THUMBNAIL_DISPLAY_TRANSFORM}/`,
      )
    : undefined;
